import {
  Ban,
  CheckCircle2,
  FileText,
  Paperclip,
  RotateCcw,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PermissionGate } from "../../../../auth/PermissionGate";
import {
  Breadcrumb,
  type BreadcrumbSegment,
} from "../../../../components/Breadcrumb";
import { Button } from "../../../../components/Button";
import { ErrorState } from "../../../../components/ErrorState";
import { PreviewTable } from "../../../../components/PreviewTable";
import { Skeleton } from "../../../../components/Skeleton";
import { ProfileBanner } from "../../../../components/profile/ProfileBanner";
import { ProfileCard } from "../../../../components/profile/ProfileCard";
import { ProfileHeader } from "../../../../components/profile/ProfileHeader";
import { ProfileHeaderShell } from "../../../../components/profile/ProfileHeaderShell";
import { ConfirmDialog } from "../../../../components/ConfirmDialog";
import { DocumentToolbar } from "../components/DocumentToolbar";
import { ShareModal } from "../components/ShareModal";
import { IncrementVersionModal } from "../components/IncrementVersionModal";
import { DocumentVersions } from "../components/DocumentVersions";
import { ConfidentialWarningModal } from "../components/ConfidentialWarningModal";
import { CountBadge } from "../../../../components/CountBadge";
import { StatusText } from "../../../../components/table/StatusText";
import { ApiError } from "../../../../lib/apiError";
import { formatBytes } from "../../../../lib/formatBytes";
import { formatFileType } from "../../../../lib/formatFileType";
import { formatDateTime, toIso } from "../../../../lib/formatDate";
import { DocumentPreview } from "../components/DocumentPreview";
import { repositoryApi } from "../repositoryApi";
import type {
  RepositoryDocumentVersionListItem,
  RepositoryItemListItem,
} from "../repositoryTypes";

type TabKey = "info" | "preview" | "versions";

/**
 * One controlled document.
 *
 * **Only a FILE gets a profile** — product call, 15 Sep 2026, and in the deviations table. A
 * folder is not a record but a PLACE: "what is this one" is answered by its contents, which is
 * what clicking it shows. A document is a record in the full sense, and this is where its
 * permanent number, its classification, the unit that registered it and its trail live.
 *
 * On the **header** layout: it reads as one continuous thing, and its second tab is a VIEW of
 * the same record rather than another region of it. The version history in Phase 3 is what
 * would earn a third.
 */
export function DocumentProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [document, setDocument] = useState<RepositoryItemListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [restoring, setRestoring] = useState(false);
  /*
    PREVIEW is the landing tab. Product call, 18 Sep 2026, and it departs from "every profile
    opens on Info" — a document is a thing somebody opens to READ, where every other record in
    this product is opened to check its fields.

    `Info` is still the FIRST tab declared, which is what the shell checks in development and
    what the rule is really about: the strip's order does not change, only where the page
    lands.

    Two consequences, both stated rather than discovered:

    - The preview FETCHES on mount, so a profile open now pulls the file — up to 50 MB — every
      time. That cost used to be paid only by a reader who asked for it.
    - A reader without `repository.download` cannot see this tab at all, and `<FolderTabs>`
      corrects an active key it cannot find to the first visible one. They land on Info, which
      is the right answer and is the strip's doing rather than a branch here.
  */
  const [tab, setTab] = useState<TabKey>("preview");

  /*
    The record-level toolbar's state.

    A pin is PERSONAL — two readers looking at one document disagree about whether it is pinned
    — and the row answers it anyway, because `IsPinned` is resolved for the CALLER on every
    read. So this seeds from the record and is only held separately so the chip can respond
    before the server has.

    It used to fetch every pin the reader holds and look for this id in it, which is a list
    fetched to answer a boolean.
  */
  const [pinned, setPinned] = useState(false);

  /**
   * Whether the reader has acknowledged that this document is confidential.
   *
   * Per VISIT, never persisted — the same call `<ProfileBanner>`'s dismissal makes. A warning
   * the reader dismissed last week is one they will not see when they open the document on a
   * shared screen today, and remembering it would quietly turn a standing notice into a
   * one-time one.
   *
   * Keyed off the record rather than shown on mount, so it fires once the page knows the
   * document IS confidential rather than on every profile.
   */
  const [confidentialAcknowledged, setConfidentialAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [revising, setRevising] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [running, setRunning] = useState(false);

  /*
    The version history, fetched with the record rather than by the tab — the tab BADGE needs
    the count whether or not the reader opens it, and a second fetch for the same list would be
    the same question asked twice.
  */
  const [versions, setVersions] = useState<RepositoryDocumentVersionListItem[]>([]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      /*
        The history rides along with the record, and a failure on it is swallowed: the tab
        renders an empty history rather than failing the page, which is the right trade for a
        list that is supplementary to the record it hangs off.
      */
      repositoryApi
        .versions(id, signal)
        .then((rows) => {
          if (!signal?.aborted) setVersions(rows);
        })
        .catch(() => undefined);

      repositoryApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setDocument(next);

          // The server's answer for THIS caller, so a refresh corrects a chip an optimistic
          // toggle left disagreeing with the record.
          setPinned(next.isPinned);

          // Cleared on success, or a failed action would keep its banner over a page that has
          // since re-read the record and disagrees with it.
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError("Could not load this document.", 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /**
   * Fetches the file through the authenticated client and hands it to the browser.
   *
   * **An object URL, not an anchor pointed at the endpoint.** A browser navigation carries no
   * bearer token, so a bare href would arrive unauthenticated — and fail silently.
   */
  async function download() {
    if (!id || !document) return;

    setError(null);

    try {
      const { blob, fileName } = await repositoryApi.download(id);

      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement("a");

      anchor.href = url;
      anchor.download = fileName ?? document.name;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError("Could not download the file.", 0),
      );
    }
  }

  async function togglePin() {
    if (!id) return;

    // Optimistic, and corrected by the response. A pin is one boolean about the caller's own
    // view, so a half-second of lag on it is worse than a flicker if the server disagrees.
    setPinned((current) => !current);

    try {
      setPinned(await repositoryApi.togglePin(id));
    } catch (caught) {
      setPinned((current) => !current);
      setError(caught instanceof ApiError ? caught : new ApiError("Could not update the pin.", 0));
    }
  }

  async function remove() {
    if (!id) return;

    setRunning(true);
    setError(null);

    try {
      await repositoryApi.deactivate(id);
      setDeleting(false);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError("Could not delete this document.", 0),
      );
    } finally {
      setRunning(false);
    }
  }

  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await repositoryApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError("Could not restore this document.", 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    "Document Management",
    { label: "Repository", to: "/repository" },
    ...(document?.parentId
      ? [
          {
            label: document.parentName ?? "Folder",
            to: `/repository?folder=${document.parentId}`,
          },
        ]
      : []),
    "Document",
  ];

  // The full-page error state is for a failure with NOTHING to show. Where a record is already
  // up it is still the last good answer, so a failed download goes to the banner instead.
  if (error && !document) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (loading && !document) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!document) {
    return null;
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="repository-document-profile"
        /*
          The record-level action strip, under the header and on screen for every tab —
          captured from GRC's DIM.

          SHARE is the one chip not passed a handler, and the component drops it rather than
          drawing a dead one. It is blocked on a visibility model rather than on an endpoint —
          see `<DocumentToolbar>`, where the reasoning is set out.
        */
        toolbar={
          <DocumentToolbar
            document={document}
            pinned={pinned}
            onDownload={() => void download()}
            onReplace={() => setRevising(true)}
            onShare={() => setSharing(true)}
            onTogglePin={() => void togglePin()}
            onDelete={() => setDeleting(true)}
          />
        }
        header={
          <ProfileHeader
            id="repository-document-profile-header"
            icon={<FileText className="h-6 w-6" aria-hidden="true" />}
            title={document.name}
            /*
              What the document IS, in one line under its name: the number it is known by,
              what it is classified as, and what kind of file it is and how big.

              The reference number leads, because it is what the document is KNOWN by — the
              name is what somebody called it, and two documents may reasonably share one.

              Every entry is dropped when the record has no value for it rather than rendering
              an em dash: this is a middot-joined line of facts, not a table of fields, and a
              dash between two middots reads as a fact that failed to load. An uncontrolled
              file carries neither of the first two and reads `PDF · 359 KB`.

              The TYPE is the verified one — see `formatFileType`. Reading it off the uploaded
              filename's extension would state the uploader's claim rather than what the bytes
              turned out to be.
            */
            meta={[
              document.referenceNumber,
              document.classificationName,
              formatFileType(document.fileContentType),
              document.fileSizeBytes === null
                ? null
                : formatBytes(document.fileSizeBytes),
              // One filter, so a value is derived once and the line cannot grow an entry the
              // guard above it forgot.
            ].filter((value): value is string => Boolean(value))}
            status={
              <>
                {/*
                  The CONFIDENTIAL pill, beside the document's own state rather than instead of
                  it — the two answer different questions, and a document is both active and
                  confidential at once.

                  It leads, because it is the fact a reader has to register before they read
                  anything else on the page. `blocked` is the red tone: `pending` is amber and
                  amber is the brand colour, which on a record's state reads as chrome the shell
                  put there rather than a fact about the document.

                  The WORD is the catalog's — "Company Confidential", "Client Confidential" —
                  never a coined one. `<StatusText>` lower-cases it, which is that component's
                  own treatment for every state in the product.
                */}
                {document.classificationIsConfidential ? (
                  <StatusText tone="blocked">
                    {document.classificationName ?? 'Confidential'}
                  </StatusText>
                ) : null}

                {!document.isActive ? (
                  <StatusText tone="inactive">Deleted</StatusText>
                ) : document.storedFileId === null ? (
                /*
                  A file with no bytes. Not reachable for anything uploaded after 17 Sep 2026 —
                  an upload writes the row and stores the bytes in one call — but rows written
                  while registration still made reservations can be in this state, and a status
                  that could not say so would show them as ordinary documents.

                  `pending` is legal off an index surface.
                */
                  <StatusText tone="pending">No file</StatusText>
                ) : (
                  <StatusText tone="active">Active</StatusText>
                )}
              </>
            }
            createdBy={document.createdByName}
            createdAt={document.createdAt}
            /*
              NO `actions`. Download was here as a header button and is the toolbar's DL chip
              directly below it — one action in two places on one screen, which is the thing
              "an action belongs to the thing it changes" exists to stop. The toolbar is the
              record-level action strip; the header states the record.
            */
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="repository-document-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : !document.isActive ? (
            <ProfileBanner
              id="repository-document-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This document is deleted. Its reference number stays permanently retired — it is never reissued — and the stored file is kept, so a restore is faithful."
              action={
                <PermissionGate permission="repository.restore">
                  <Button
                    id="repository-document-restore"
                    variant="secondary"
                    leading={
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    }
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          ) : document.storedFileId === null ? (
            /*
              A row from before uploads created the file and the bytes together — registration
              used to allocate a number and leave the slot for Attach to fill.

              It carries NO action, and that is the honest state rather than an oversight: the
              way to put a file on a record is to upload one, and replacing or supplying a file
              on an existing row is a VERSION, which is Phase 3. Offering a button that cannot
              exist yet would be worse than saying what is true.
            */
            <ProfileBanner
              id="repository-document-profile-banner"
              tone="amber"
              icon={<Paperclip className="h-4 w-4" aria-hidden="true" />}
              message="This document has no file. It was registered before uploads created the record and its file together, and supplying one is a new version."
            />
          ) : (
            <ProfileBanner
              id="repository-document-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This document's file is stored. The reference number is permanent and is never reissued."
            />
          )
        }
        /*
          PREVIEW carries `repository.download`, not `repository.read`.

          That is the same split the endpoint behind it takes, and the reason is the reason the
          key exists: knowing a controlled document is there and being handed its contents are
          different privileges. A reader with read alone gets the record and no tab — hidden
          rather than disabled, because it is a gate and not a state, and `<FolderTabs>` lands
          them on Info by itself.

          The tab is drawn even when there is nothing to show. A document with no file, or one
          whose type cannot be rendered, is a question the reader asks by opening it — and the
          panel answers in a sentence, which a tab that vanished could not.
        */
        tabs={[
          { key: "info", label: "Info" },
          {
            key: "preview",
            label: "Preview",
            permission: "repository.download",
          },
          {
            key: "versions",
            /*
              The COUNT goes inside the label, never as a separate prop — and a zero renders,
              because that is what tells a reader the document is still on its first issue
              before they click. It counts SUPERSEDED files, so a document at v1 reads 0.
            */
            label: (
              <>
                Versions <CountBadge count={versions.length} />
              </>
            ),
            permission: "repository.read",
          },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        /*
          The preview owns its own chrome and wants the whole height — a page of document inside
          a card capped to a readable measure is a letterbox. The cards keep `cards`, which is
          what that cap is for.
        */
        panel={tab === "preview" || tab === "versions" ? "bleed" : "cards"}
      >
        {tab === "versions" ? (
          <DocumentVersions document={document} versions={versions} />
        ) : tab === "preview" ? (
          <DocumentPreview
            document={document}
            onDownload={() => void download()}
          />
        ) : (
          <>
            <ProfileCard id="repository-document-details" title="Details">
              <PreviewTable
                variant="table-display"
                rows={[
                  {
                    label: "Reference number",
                    value: document.referenceNumber,
                    mono: true,
                  },
                  { label: "Name", value: document.name },
                  {
                    label: "Classification",
                    value: document.classificationName,
                  },
                  {
                    label: "Originating unit",
                    /*
                  A LIVE view: this is the unit's CURRENT name, while the business-unit code
                  inside the reference number is the snapshot. The two disagreeing after a
                  recode is the system working rather than a fault.
                */
                    value: document.originatingOrgUnitName,
                  },
                  { label: "Folder", value: document.parentName },
                  { label: "Description", value: document.description },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="repository-document-file" title="File">
              <PreviewTable
                variant="table-display"
                rows={[
                  {
                    label: "Stored",
                    value: document.storedFileId === null ? "No file" : "Yes",
                  },
                  { label: "Uploaded as", value: document.fileOriginalName },
                  {
                    label: "Size",
                    value:
                      document.fileSizeBytes === null
                        ? null
                        : formatBytes(document.fileSizeBytes),
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id="repository-document-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: "Created by", value: document.createdByName },
                  {
                    label: "Created",
                    value: (
                      <time
                        dateTime={toIso(document.createdAt)}
                        className="font-mono"
                      >
                        {formatDateTime(document.createdAt)}
                      </time>
                    ),
                  },
                  { label: "Updated by", value: document.updatedByName },
                  {
                    label: "Updated",
                    value: document.updatedAt ? (
                      <time
                        dateTime={toIso(document.updatedAt)}
                        className="font-mono"
                      >
                        {formatDateTime(document.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {/*
        The CONFIDENTIAL warning, shown once the record says it is one.

        It gates NOTHING — the reader already holds `repository.read` on this branch and the
        server settled that before the page rendered. What it adds is that they know what they
        are looking at before they read it, forward it, or leave it on a screen. Treating it as
        protection would be treating a notice as a permission.

        Not persisted: a warning acknowledged last week is one they would not see when they open
        the document on a shared screen today.
      */}
      <ConfidentialWarningModal
        open={document.classificationIsConfidential && !confidentialAcknowledged}
        classificationName={document.classificationName}
        documentName={document.name}
        onAcknowledge={() => setConfidentialAcknowledged(true)}
      />

      {sharing ? (
        /*
          Reloads on save because a share can change what THIS reader sees: granting somebody
          else access does not, but changing general access on a folder they are standing in
          does, and the dialog cannot tell which happened.
        */
        <ShareModal
          open
          itemId={document.id}
          onClose={() => setSharing(false)}
          onSaved={load}
        />
      ) : null}

      {revising ? (
        <IncrementVersionModal
          open
          document={document}
          onClose={() => setRevising(false)}
          onSaved={() => {
            setRevising(false);
            load();
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          open
          id="repository-document-delete-dialog"
          onClose={() => {
            setDeleting(false);
            setError(null);
          }}
          onConfirm={() => void remove()}
          title="Delete document"
          danger
          /*
            Both safeguards, because this is destructive: the tick proves somebody clicked, the
            password proves it is still the person who signed in. The undo — Restore, in the
            banner — takes neither.
          */
          requireConfirm
          requireCredential
          loading={running}
          body={
            <div className="flex flex-col gap-2">
              <p>&quot;{document.name}&quot; is hidden from the repository.</p>

              <p>
                Nothing is erased — the document can be restored from here, and its stored file
                is kept so the restore is faithful. Its reference number stays retired either
                way and is never reissued.
              </p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
